import { z } from "zod";

const EnvSchema = z.object({
  NODE_ENV: z.enum(["development", "test", "production"]).default("production"),
  NEXT_PUBLIC_SITE_URL: z.string().url().default("http://localhost:3000"),
  NEXT_PUBLIC_CDN_URL: z.string().url().default("https://cdn.pickcel.com"),
  NEXT_PUBLIC_MIXPANEL_TOKEN: z.string().optional().default(""),
  NEXT_PUBLIC_GTM_ID: z.string().optional().default(""),
  NEXT_PUBLIC_ZOHO_WIDGET_CODE: z.string().optional().default(""),
  NEXT_PUBLIC_THINKSTACK_CHATBOT_ID: z.string().optional().default(""),
  NEXT_PUBLIC_HOSTED_SITE_URL: z.string().optional().default("https://www.pickcel.com"),
  NEXT_PUBLIC_GRECAPTCHA_KEY: z.string().optional().default("6Lf40aMUAAAAALaRwGBh4aR8mQUtrdI7UBM0jft7"),
  NEXT_PUBLIC_THINKSTACK_API_URL: z.string().url().default("https://app.thinkstack.ai"),
  NEXT_PUBLIC_WIDGET_ENDPOINT: z.url().default("https://testwidget.pickcel.com/verify-recaptcha"),
});

export type AppEnv = z.infer<typeof EnvSchema>;

// Determine ThinkStack API URL based on environment
const getThinkStackApiUrl = () => {
  const nodeEnv = process.env.NODE_ENV || "production";
  return nodeEnv === "production"
    ? "https://app.thinkstack.ai"
    : "https://app.dev.thinkstack.ai";
};

export const env: AppEnv = EnvSchema.parse({
  NODE_ENV: process.env.NODE_ENV,
  NEXT_PUBLIC_SITE_URL: process.env.NEXT_PUBLIC_SITE_URL,
  NEXT_PUBLIC_CDN_URL: process.env.NEXT_PUBLIC_CDN_URL,
  NEXT_PUBLIC_MIXPANEL_TOKEN: process.env.NEXT_PUBLIC_MIXPANEL_TOKEN,
  NEXT_PUBLIC_GTM_ID: process.env.NEXT_PUBLIC_GTM_ID,
  NEXT_PUBLIC_ZOHO_WIDGET_CODE: process.env.NEXT_PUBLIC_ZOHO_WIDGET_CODE,
  NEXT_PUBLIC_THINKSTACK_CHATBOT_ID: process.env.NEXT_PUBLIC_THINKSTACK_CHATBOT_ID,
  NEXT_PUBLIC_HOSTED_SITE_URL: process.env.NEXT_PUBLIC_HOSTED_SITE_URL,
  NEXT_PUBLIC_GRECAPTCHA_KEY: process.env.NEXT_PUBLIC_GRECAPTCHA_KEY,
  NEXT_PUBLIC_THINKSTACK_API_URL: getThinkStackApiUrl(),
  NEXT_PUBLIC_WIDGET_ENDPOINT: process.env.NEXT_PUBLIC_WIDGET_ENDPOINT,
});

// The only host that should be crawled/indexed by search engines.
export const PRODUCTION_SITE_HOST = "www.pickcel.com";

/**
 * True only for the public production deployment (www.pickcel.com in a
 * production build). Every other host — website.internal.pickcel.com,
 * preview/staging deployments, and local dev — is treated as non-indexable
 * so it never leaks into search results.
 */
export const isProductionSite: boolean =
  env.NODE_ENV === "production" &&
  (() => {
    try {
      return new URL(env.NEXT_PUBLIC_HOSTED_SITE_URL).hostname === PRODUCTION_SITE_HOST;
    } catch {
      return false;
    }
  })();
